import { useEffect, useState } from 'react';
import { Logger } from '@saviynt/common';

import { useInvertedLocalize } from '../utils/localization';

import invertedMessages from './messages/ignoreBanner';

interface IgnoreBannerReturn {
  ignoreBanner: {
    isVisible: boolean;
    message: string;
    colorTheme: 'Success' | 'Critical';
    onClose: () => void;
  };
}

/**
 * Custom hook for managing ignore role banners
 * Checks sessionStorage for ignore success/error data and manages banner state
 */
const useIgnoreBanner = (): IgnoreBannerReturn => {
  const l = useInvertedLocalize(invertedMessages);

  const [showIgnoreBanner, setShowIgnoreBanner] = useState(false);
  const [ignoreMessage, setIgnoreMessage] = useState('');
  const [ignoreColorTheme, setIgnoreColorTheme] = useState<
    'Success' | 'Critical'
  >('Success');

  // Check for role ignore data from sessionStorage
  useEffect(() => {
    try {
      // Check for success data
      const successData = sessionStorage.getItem('roleIgnoreSuccess');

      if (successData) {
        const parsed = JSON.parse(successData) as {
          timestamp: number;
        };
        const { timestamp } = parsed;

        // only show if recent (prevent stale data from prior crashes)
        const isRecent = Date.now() - timestamp < 60000; // 1 minute

        if (isRecent) {
          setIgnoreMessage(
            l('The role recommendation was successfully ignored.')
          );
          setIgnoreColorTheme('Success');
          setShowIgnoreBanner(true);
        }

        sessionStorage.removeItem('roleIgnoreSuccess'); // always clean up
      }

      // Check for error data
      const errorData = sessionStorage.getItem('roleIgnoreError');

      if (errorData) {
        const parsed = JSON.parse(errorData) as {
          timestamp: number;
        };
        const { timestamp } = parsed;

        const isRecent = Date.now() - timestamp < 60000; // 1 minute

        if (isRecent) {
          setIgnoreMessage(
            l('The role could not be ignored due to an internal error.')
          );
          setIgnoreColorTheme('Critical');
          setShowIgnoreBanner(true);
        }

        sessionStorage.removeItem('roleIgnoreError');
      }
    } catch (parseError) {
      Logger.error('Error processing ignore role recommendation:', parseError);
      // clean up corrupted data
      sessionStorage.removeItem('roleIgnoreSuccess');
      sessionStorage.removeItem('roleIgnoreError');
    }
  }, [l]);

  return {
    ignoreBanner: {
      isVisible: showIgnoreBanner,
      message: ignoreMessage,
      colorTheme: ignoreColorTheme,
      onClose: () => setShowIgnoreBanner(false),
    },
  };
};

export default useIgnoreBanner;
